<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header variant="cross" :saving="loading" @cancel="closeModal()">
        <h1>{{ $t('pages.campaigns.driverList.modal.specialDriverAssignment.title') }}</h1>
      </modal-header>
    </template>

    <form @submit="save">
      <div class="mt-8">
        <div class="flex flex-col w-full">
          <div class="flex flex-col gap-y-5">
            <Multiselect
              v-model="rawCampaignRegistration.campaignRegistration"
              :error="errors?.campaignRegistration"
              class="col-start-1 col-end-2"
              :options="driverList"
              :label="$t('campaignRegistration.driver')"
              :can-deselect="true"
              :can-clear="false"
              :searchable="true"
              :min-chars="2"
              :filterResults="false"
              :delay="500"
              :resolve-on-load="true"
              :create-option="false"
              :clear-on-select="true"
              :no-options-text="$t('inputSearchString')"
              :object="true"
              @select="select"
              @clear="clear"
            />
            <MultiselectTags
              v-model="rawCampaignRegistration.specialDriverAssignments"
              :options="specialAssignmentTypeOptions"
              :error="errors?.specialDriverAssignment.type"
              :label="$t('campaignRegistration.specialDriverAssignment.types')"
              :can-deselect="true"
              :can-clear="false"
              :disabled="rawCampaignRegistration.campaignRegistration === undefined"
              mode="tags"
              class="mt-3 mb-5"
            />
          </div>
          <div class="w-full text-center flex gap-x-5 pt-5 justify-center">
            <CustomButton
              v-if="props.campaignRegistration?.specialDriverAssignments.length > 0"
              :busy="removing"
              variant="outlined"
              @click="removeSpecialAssignment"
            >
              {{ $t('pages.campaigns.driverList.actions.removeSpecialDriverAssignment') }}
            </CustomButton>
            <custom-button
              variant="primary"
              :busy="loading"
              @click="save"
            >
              {{ $t('save') }}
            </custom-button>
          </div>
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import {
  array,
  object,
  string,
} from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import Multiselect from '@utils/Multiselect.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import CustomButton from '@utils/CustomButton.vue';
import { collOptionsProvider, enumOptionsProvider, type ProviderParams } from '@utils/table/hooks/provider';
import MultiselectTags, { type Option } from '@utils/MultiselectTags.vue';
import { useDeletePrompt } from '@utils/prompt/injects';
import type {
  CampaignGroup,
  CampaignRegistration,
  SpecialDriverAssignmentType,
} from '@/entities/Entities';

interface Props {
  open: boolean;
  campaignGroup: CampaignGroup;
  campaignRegistration?: CampaignRegistration | undefined;
}

type DriverOption = Option & {
  specialDriverAssignments: {
    type: SpecialDriverAssignmentType;
    completed?: boolean;
  }[];
}

type RawCampaignRegistration = {
  iri: string | undefined;
  campaignRegistration: DriverOption | undefined;
  specialDriverAssignments: {
    label: string;
    value: SpecialDriverAssignmentType;
    completed?: boolean;
  }[];
};

const emit = defineEmits(['update:open', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);

const { t } = useI18n();

const notify = useNotify();

const specialAssignmentTypeOptions = enumOptionsProvider('/enums/specialAssignmentType', (v) => ({
  label: t(`campaignRegistration.specialDriverAssignment.type.${v}`),
  value: v,
}));

const initialData = (): RawCampaignRegistration => ({
  iri: props.campaignRegistration?.['@id'] || undefined,
  campaignRegistration: props.campaignRegistration ? {
    label: `${props.campaignRegistration.driver.user.firstName} ${props.campaignRegistration.driver.user.lastName}`,
    value: props.campaignRegistration['@id'],
    specialDriverAssignments: props.campaignRegistration?.specialDriverAssignments,
  } : undefined,
  specialDriverAssignments: props.campaignRegistration?.specialDriverAssignments.map((item) => ({
    label: t(`campaignRegistration.specialDriverAssignment.type.${item.type}`),
    value: item.type,
    completed: item.completed,
  })) || [],
});

const rawCampaignRegistration = ref<RawCampaignRegistration>({ ...initialData() });

watch(() => props.campaignRegistration, () => {
  rawCampaignRegistration.value = {
    ...initialData(),
  };
}, { deep: true });

const driverList = async (query: string, select: any) => {
  if (props.campaignRegistration) {
    return [{
      label: `${props.campaignRegistration.driver.user.firstName} ${props.campaignRegistration.driver.user.lastName}`,
      value: props.campaignRegistration['@id'],
    }];
  }
  const input = query || select.modelValue;
  if (!input) return Promise.resolve([]);

  const filterQuery: ProviderParams = {
    filter: query ? { 'driver.user.fullName': input, 'campaign.group': props.campaignGroup?.id } : { id: select.modelValue },
  };
  return collOptionsProvider<CampaignRegistration>('/campaign_registrations/search', filterQuery, (value) => ({
    label: value.driver?.user?.fullName,
    value: value['@id'],
    specialDriverAssignments: value.specialDriverAssignments,
  }))();
};

const {
  data: campaignRegistration,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    iri: string().required(t('validation.notEmpty')),
    campaignRegistration: object().required(t('validation.notEmpty')),
    specialDriverAssignment: array().of(object({
      type: string().required(t('validation.typeRequired')),
    })),
  }),
  rawCampaignRegistration,
);

function resetData() {
  rawCampaignRegistration.value = { ...initialData() } as unknown as typeof campaignRegistration['value'];
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
}

const select = (registration: DriverOption|null): void => {
  const specialDriverAssignments = registration?.specialDriverAssignments;
  console.log('select', registration?.value, registration);
  if (specialDriverAssignments) {
    rawCampaignRegistration.value = {
      iri: registration?.value,
      campaignRegistration: registration,
      specialDriverAssignments: specialDriverAssignments.map((item) => ({
        label: t(`campaignRegistration.specialDriverAssignment.type.${item.type}`),
        value: item.type,
      })),
    };
  } else {
    rawCampaignRegistration.value = {
      iri: registration?.value,
      campaignRegistration: registration,
      specialDriverAssignments: [],
    };
  }
};

const clear = () => {
  rawCampaignRegistration.value = {
    id: undefined,
    campaignRegistration: undefined,
    specialDriverAssignments: [],
  };
};

const [exec, { loading }] = useFetchAction();

const save = validatedFn(async () => {
  try {
    await exec(rawCampaignRegistration.value.iri, {
      method: 'PUT',
      body: {
        specialDriverAssignments: rawCampaignRegistration.value.specialDriverAssignments.map((item) => ({
          type: item.value,
          completed: item.completed || false,
        })),
      },
    });
    notify?.({
      variant: 'success',
      title: t('pages.campaigns.driverList.modal.new.saveSuccess'),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t('pages.campaigns.driverList.modal.new.saveError'),
    });
  }
});

const promptDelete = useDeletePrompt();

const removeSpecialAssignment = async () => {
  try {
    const answer = await promptDelete?.();
    if (answer && rawCampaignRegistration.value.iri) {
      await exec(rawCampaignRegistration.value.iri, {
        method: 'PUT',
        body: {
          specialDriverAssignments: [],
        },
      });

      notify?.({
        variant: 'success',
        title: t('pages.campaigns.driverList.modal.new.saveSuccess'),
      });
      emit('saved');
      closeModal();
    }
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t('pages.campaigns.driverList.modal.new.saveError'),
    });
  }
};
</script>

<style lang="scss" scoped></style>
